import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '@com/core/common/translations/i18n';
import Link from '@com/core/common/components/Link';
import BlockButton from './BlockButton';

type TProps = {
    ctaClick?: () => void;
    type?: string | number;
};

const EmptyState = ({ ctaClick = null, type }: TProps): JSX.Element => {
    const { t } = useTranslation('common');

    let heading;
    let subheading;
    let ctaUrl;
    let ctaLabel;
    let imgUrl;
    let imgAlt;
    switch (true) {
        case type === 'cart':
            heading = t('_emptyState_cartHeading');
            subheading = t('_kul-emptyState-cartSubCopy');
            ctaUrl = '/';
            ctaLabel = t('_kul-emptyState-cart-button');
            imgUrl = '/images/emptyStates/cart.png';
            imgAlt = 'empty-cart';
            break;
        case type === 'product':
            heading = t('_emptyState_productHeading');
            subheading = t('_emptyState_productSubCopy');
            ctaUrl = '/';
            ctaLabel = t('continue-shopping');
            imgUrl = '/images/emptyStates/product.png';
            imgAlt = 'empty-search';
            break;
        case type === 'addresses':
            heading = t('_error_empty-addresses-title');
            subheading = t('_error_empty-addresses-subtitle');
            ctaUrl = '/';
            ctaLabel = t('add-new-address');
            imgUrl = '/images/emptyStates/addresses.png';
            imgAlt = 'empty-addresses';
            break;
        case type === 'payments':
            heading = 'No Saved Cards';
            subheading = 'Cards that you have checked out with and saved will appear here';
            ctaUrl = '/';
            ctaLabel = t('continue-shopping');
            imgUrl = '/images/emptyStates/payments.png';
            imgAlt = 'empty-payments';
            break;
        case type === 'orders':
            heading = t('_error_empty-orders-title');
            subheading = t('_error_empty-orders-subtitle');
            ctaUrl = '/';
            ctaLabel = t('continue-shopping');
            imgUrl = '/images/emptyStates/error.png';
            imgAlt = 'empty-orders';
            break;
        case type === 'autoDeliveries':
            heading = 'No auto deliveries are upcoming';
            subheading = 'Items that you schedule or subscribe to will display here';
            ctaUrl = '/';
            ctaLabel = t('continue-shopping');
            imgUrl = '/images/emptyStates/auto-deliveries.png';
            imgAlt = 'empty-auto-deliveries';
            break;
        case type === 'subscriptions':
            heading = 'No subscriptions added for now';
            subheading = 'Subscribe for items you want and get auto deliveries at your doorstep';
            // ctaUrl = '/';
            // ctaLabel = t('continue-shopping');
            imgUrl = '/images/emptyStates/auto-deliveries.png';
            imgAlt = 'empty-auto-deliveries';
            break;
        case type === 'credits':
            heading = 'No Noon Credits';
            subheading = t('_credits_blank-slate-title');
            ctaUrl = '/';
            ctaLabel = t('continue-shopping');
            imgUrl = '/images/emptyStates/credits.png';
            imgAlt = 'empty-credits';
            break;
        case type === 'orderHelp':
            heading = t('_emptyState_orderHelpHeading');
            subheading = t('_emptyState_orderHelpSubHeading');
            ctaUrl = 'https://help.noon.com/hc/en-us/sections/4405362385943-Return-and-Refund';
            ctaLabel = t('need-more-help');
            imgUrl = '/images/emptyStates/orderHelp.svg';
            imgAlt = 'empty-order-help';
            break;
        case type === 'complains':
            heading = t('no_complaints');
            subheading = t('no_complaints_description');
            imgUrl = '/creativeIcons/no-complains.svg';
            imgAlt = 'no-complaints';
            break;
        case type === 'returns-requested':
            heading = t('no_returns_requested');
            subheading = t('no_returns_requested_description');
            imgUrl = '/creativeIcons/no-returns-requested.svg';
            imgAlt = 'no-returns-requested';
            break;
        case type === 'returns-approved':
            heading = t('no_returns_approved');
            subheading = t('no_returns_approved_description');
            imgUrl = '/creativeIcons/no-returns-approved.svg';
            imgAlt = 'no-returns-approved';
            break;
        case type === 404:
            heading = t('_kul-emptyState_404HeadingMissing');
            subheading = t('_emptyState_404SubCopy');
            ctaUrl = '/';
            ctaLabel = t('_kul-emptyState-go-back-to-homepage');
            imgUrl = '/images/emptyStates/error.png';
            imgAlt = 'error-404';
            break;
        default:
            heading = t('_emptyState_5xxHeadingWrong');
            subheading = t('_emptyState_5xxSubCopy');
            ctaUrl = '/';
            ctaLabel = t('continue-shopping');
            imgUrl = '/images/emptyStates/error.png';
            imgAlt = 'error-500';
    }
    return (
        <Container>
            <div className="siteWidthContainer">
                <Image src={imgUrl} alt={imgAlt} />
                <Heading>{heading}</Heading>
                <Subheading>{subheading}</Subheading>
                {ctaClick && ctaLabel && (
                    <div className="ctaContainer">
                        <BlockButton onClick={ctaClick}>{ctaLabel}</BlockButton>
                    </div>
                )}
                {!ctaClick && ctaUrl && ctaLabel && (
                    <Cta>
                        <Link href={ctaUrl} ariaLabel={ctaLabel} linkClass="ctaContent">
                            {ctaLabel}
                        </Link>
                    </Cta>
                )}
            </div>
        </Container>
    );
};

const Container = styled.div`
    padding: 64px 0;
    text-align: center;

    .siteWidthContainer {
        max-width: 1000px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .ctaContainer {
        margin-top: 24px;
    }
`;

const Image = styled.img`
    width: 40%;
    max-width: 320px;
`;

const Heading = styled.h1`
    font-weight: bold;
    font-size: 1.5rem;
    margin-top: 30px;
`;

const Subheading = styled.p`
    color: ${({ theme }): string => `${theme.colors.bodyLight}`};
    margin-top: 8px;
`;

const Cta = styled.div`
    margin-top: 24px;
    white-space: nowrap;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    color: ${({ theme }): string => theme.colors.brandPrimary};
    border: 1px solid ${({ theme }): string => theme.colors.brandPrimary};
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    position: relative;
    overflow: hidden;
    transition: all ease-in-out 0.2s;

    &:after {
        content: '';
        position: absolute;
        bottom: 0;
        ${({ theme: { left } }): string => `${left}: 0`};
        width: 0%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.15);
        transition: all ease-in-out 0.2s;
        z-index: 1;
    }

    &:active {
        position: relative;
        top: 1px;
        right: -1px;
    }

    &:hover {
        &:after {
            width: 100%;
        }
    }

    .ctaContent {
        font-size: 1rem;
        font-weight: bold;
        text-align: center;
        position: relative;
        z-index: 2; /* for hover effect to work "behind" it */
        height: 48px;
        padding: 0 64px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export default EmptyState;
